/**
 * atlaspanel.jsx — the ship, in three dimensions, inside the chapter that teaches on her.
 *
 * FOUNDER, 2026-08-18: "in fact all of these chapters wherever shall have 3D rendering" — and the
 * standing ruling it sits under, from 2026-08-01: "a 3d is like a model not hallucinated …
 * visualize a sphere, meridian, ship parts like girders — not visual LLM model but geometries."
 *
 * ── NOTHING HERE ASKS A MODEL TO DRAW A SHIP ─────────────────────────────────────────────────
 * The view is the estate's existing atlas, whose every surface is a consequence of three declared
 * inputs — the table of offsets, SCANTLINGS and ARRANGEMENT. Chapter 6 of NavalArch101 is entirely
 * about why that distinction is load-bearing: unlike a wrong sentence, a picture carries no
 * citation a reader can follow, so a generated one is worse than generated prose rather than
 * better. This panel is a lookup and a transform over a fixed model.
 *
 * ── IT EMBEDS THE ATLAS RATHER THAN REDRAWING IT ─────────────────────────────────────────────
 * The obvious build is a second renderer in React. That would be a second copy of the geometry
 * pipeline, and this estate has spent the week removing second copies — KM computed from KB and BM
 * rather than stated beside them, the catalogue's inverse edge derived, build stages computed from
 * dependencies, the model answer rendered from the same geometry the marking uses. So the panel
 * frames /atlas, same-origin, and what a learner sees in a chapter is the same object the Ship
 * surface shows. There is nothing to drift.
 *
 * ── A VIEW WITH NO STATED REASON IS SCENERY ──────────────────────────────────────────────────
 * The ship is DECLARED per chapter on the ladder, with a sentence saying what the view is for.
 * Three of NavalArch101's nine chapters declare, in writing, that they carry no view and why — a
 * refusal is rendered as a refusal here rather than as an empty space, because an absent panel and
 * a deliberate omission look identical otherwise.
 */
import { useState } from 'react';
import { useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { API } from './surfaces.jsx';

const W = {
  atlas: { slug: 'chapter-atlas', layout: 'auto-table',
           wire: { kind: 'rest', route: '/api/chapter-atlas', method: 'GET', field: 'ship' } },
};

export default function AtlasPanel({ level, chapter }) {
  const [open, setOpen] = useState(false);
  const q = useWidgetData(W.atlas, chapter ? { chapter, level } : {}, { baseUrl: API, plan });
  const d = q.data;
  if (!chapter || !d) return null;

  /* A gated chapter has already been refused above by the chapter's own invitation; repeating the
     refusal here would tell a learner twice that he cannot read something he already knows he
     cannot read. */
  if (d.kind === 'gated') return null;

  if (d.kind === 'null' || !d.ship) {
    return (
      <div className="g-glass shell-panel atlas-none">
        <div className="g-label">No ship shown here</div>
        <p className="g-body">{d.reason}</p>
      </div>
    );
  }

  return (
    <div className="g-glass shell-panel">
      <div className="g-label">
        The ship, computed · {String(d.ship).toUpperCase()}
        {d.status === 'proposed' ? <span className="atlas-prop"> · pairing proposed, not ruled</span> : null}
      </div>
      <p className="g-body">{d.shows}</p>

      {/* Loaded on request. The atlas builds a model of several hundred surfaces; mounting six of
          them because a learner opened a ladder would cost him a page load he did not ask for. */}
      {open ? (
        <div className="atlas-frame">
          <iframe
            src={d.view}
            title={`${String(d.ship).toUpperCase()} — computed model`}
            loading="lazy"
          />
        </div>
      ) : (
        <button type="button" className="g-btn atlas-open" onClick={() => setOpen(true)}>
          Open the model of {String(d.ship).toUpperCase()}
        </button>
      )}

      <p className="g-body g-dim atlas-note">
        {d.computed} What the model does <b>not</b> hold is listed inside it, and that list is the
        point rather than an apology.{' '}
        <a href={d.view} target="_blank" rel="noreferrer">Open it full screen →</a>
      </p>
    </div>
  );
}
